/**
 * ? @description : Teachers Training Component
 */

// Dependencies
import React, { Fragment, useState, useEffect } from "react";
import MUIDataTable, { TableFilterList } from "mui-datatables";
import { Box, Chip, CircularProgress } from "@mui/material";

// Modals
import IndividualData from "./modals/IndividualData";

// Axios Component
import Axios from "../../../../axios/Axios";
import axios from "axios";
import dayjs from "dayjs";

function TeacherTraining() {
  // * State Variables
  const [teachersData, setTeachersData] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [teacherModal, setTeacherModal] = useState(false);
  const [isDataFetchError, setIsDataFetchError] = useState(false);

  // for pagination state 
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
  };
  

  // * Fetching Data
  useEffect(() => {
    const controller = axios.CancelToken.source();
    async function fetchTeachersData() {
      try {
        const options = {
          method: "get",
          url: "teacher/get/teacherstrainingdata",
          auth: true,
          data: null,
          token:{cancelToken: controller.token}
        };
  
        const response = await Axios(
          options.method,
          options.url,
          options.auth,
          options.data,
          options.token,
        );
        // console.log(response);
        if(response.status === 200 && Array.isArray(response.data)){ 
          response.data.length !== 0 ? setTeachersData(response.data) : setIsDataFetchError(true);
        }else {
          setIsDataFetchError(true);
        }
      } catch (error) {
        if(!axios.isCancel(error)){
          setIsDataFetchError(true)
          console.log(error);
        }
      }
    }
    fetchTeachersData();
    return () => {
      controller.cancel();
  }
  }, [rowsPerPage]);

  // * Column Declarations
  const columns = [
    {
      name: "teacher_id",
      label: "Teacher Id",
      options: {
        display: false,
        filter: true,
        filterType: "Dropdown",
        sort: true,
        sortDirection: "desc",
        download: true,
        setCellProps: () => ({align: "center"}),
        customBodyRender: value => value ? value : "-"
      },
    },
    {
      name: "teacher_name",
      label: "Teacher Name",
      options: {
        filter: true,
        filterType: "Dropdown",
        sort: true,
        download: true,
        customBodyRender: (value) => {
          return (
            value ?
              <Chip
                label={value}
                color="primary"
                size="small"
                clickable
                onClick={() => setTeacherModal(true)}
              /> : "-"
          );
        },
      },
    },
    {
      name: "batch_id",
      label: "Batch Id",
      options: {
        display: false,
        filter: true,
        filterType: "Dropdown",
        sort: true,
        download: true,
        setCellProps: () => ({align: "center"}),
        customBodyRender: value => value ? value : "-"
      },
    },
    {
      name: "batch_names",
      label: "Batch Name",
      options: {
        filter: true,
        filterType: "Dropdown",
        sort: true,
        download: true,
        setCellProps: () => ({align: "center"}),
        customBodyRender: (value) =>
        value ? value.replace(/\b\w/g, (char) => char.toUpperCase()) : "-",
      },
    },
    {
      name: "audio_status",
      label: "Audio Status",
      options: {
        filter: true,
        filterType: "Dropdown",
        sort: true,
        download: true,
        setCellProps: () => ({align: "center"}),
        customBodyRender: value => value ? value : "-"
      },
    },
    {
      name: "training_video_status",
      label: "Training Video Status",
      options: {
        display:false,
        filter: true,
        filterType: "Dropdown",
        sort: true,
        download: true,
        setCellProps: () => ({align: "center"}),
        customBodyRender: value => value ? value : "-"
      },
    },
    {
      name: "video_viewed_time",
      label: "Video Viewed Time",
      options: {
        display:false,
        filter: true,
        filterType: "Dropdown",
        sort: true,
        download: true,
        setCellProps: () => ({align: "center"}),
        customBodyRender: value => value ? value : "-"
      },
    },
    {
      name: "signup_status",
      label: "Signed up status",
      options: {
        filter: true,
        filterType: "Dropdown",
        sort: true,
        download: true,
        setCellProps: () => ({align: "center"}),
        customBodyRender: value => !!value ? <Chip color="success" label="Signed up"/> : "-"
      },
    },
    {
      name: "role",
      label: "Role",
      options: {
        filter: true,
        filterType: "Dropdown",
        sort: true,
        download: true,
        setCellProps: () => ({align: "center"}),
        customBodyRender: value => value ? value : "-"
      },
    },
    {
      name: "tcreated_at",
      label: "Registered Date",
      options: {
        filter: true,
        filterType: "Dropdown",
        sort: true,
        download: true,
        setCellProps: () => ({align: "center"}),
        customBodyRender: (value) => {
          const formattedDate = value? dayjs(value).format("DD-MM-YYYY") : '-';
          return <div>{formattedDate}</div>;
        },
      },
    },
    {
      name: "batch_member_id",
      label: "Batch Member Id",
      options: {
        filter: true,
        filterType: "Dropdown",
        sort: true,
        download: true,
        display: false,
        setCellProps: () => ({align: "center"}),
        customBodyRender: value => value ? value : "-"
      },
    },
  ];

  // * Options for the Table Configuration
  const options = {
    textLabels: {
      body: {
        noMatch: isDataFetchError ? "Sorry, No Data available" : <CircularProgress />,
      }
    },
    filter: true,
    filterType: "dropdown",
    responsive: "simple",
    elevation: 0,
    rowsPerPage: rowsPerPage,
    onChangeRowsPerPage: handleRowsPerPageChange ,
    rowsPerPageOptions: [5, 10, 20],
    jumpToPage: true,
    fixedHeader: true,
    fixedSelectColumn: true,
    tableBodyHeight: "auto",
    onRowClick: (rowData) => {
      setSelectedRowData(rowData);
    },
  };

  const CustomChip = ({ label, onDelete }) => {
    return (
      <Chip
        variant="outlined"
        color="secondary"
        label={label}
        onDelete={onDelete}
      />
    );
  };

  const CustomFilterList = (props) => {
    return <TableFilterList {...props} ItemComponent={CustomChip} />;
  };

  return (
    <Fragment>
      {/* Table */}
      <Box sx={{padding: "10px"}}>
        <MUIDataTable
          title="Teachers Training Data"
          data={teachersData}
          columns={columns}
          options={options}
          components={{ TableFilterList: CustomFilterList }}
        />
      </Box>

      {/* Modal */}
      <IndividualData
        open={teacherModal}
        onClose={() => setTeacherModal(false)}
        selecteddata={selectedRowData}
      />
    </Fragment>
  );
}

export default TeacherTraining;
